/**
 * Build per-country campaign picklists for multi-country bulk edit from draft rows.
 */

import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';

export function buildCampaignsByCountryFromDrafts(
  drafts: PreparedDrop[],
): Record<string, Array<{ id: number; name: string }>> {
  const byCountry = new Map<string, Map<number, string>>();
  for (const d of drafts) {
    if (d.status !== 'DRAFT') continue;
    let m = byCountry.get(d.countryKey);
    if (!m) {
      m = new Map();
      byCountry.set(d.countryKey, m);
    }
    if (!m.has(d.wiserId)) {
      m.set(d.wiserId, d.campaignName?.trim() || `Campaign ${d.wiserId}`);
    }
  }
  const out: Record<string, Array<{ id: number; name: string }>> = {};
  for (const [ck, m] of byCountry) {
    out[ck] = [...m.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }
  return out;
}

export function mergeCampaignOptionsWithTabList(
  fromDrafts: Array<{ id: number; name: string }>,
  tabCampaigns: Array<{ id: number; name: string }>,
): Array<{ id: number; name: string }> {
  const byId = new Map(fromDrafts.map((c) => [c.id, c.name]));
  for (const c of tabCampaigns) {
    if (!byId.has(c.id)) byId.set(c.id, c.name);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}

/** Merge any number of campaign lists by id (drafts + API + tab, etc.). */
export function mergeCampaignLists(
  ...lists: Array<Array<{ id: number; name: string }>>
): Array<{ id: number; name: string }> {
  let acc: Array<{ id: number; name: string }> = [];
  for (const list of lists) {
    if (list.length === 0) continue;
    acc = mergeCampaignOptionsWithTabList(acc, list);
  }
  return acc;
}
